import PropTypes from 'prop-types';
import React, { useCallback, memo } from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import ConversationCard2 from '../ConversationCard2';

import { CreateConversation } from '@icons/common';
import ChainSkeleton from '../ChainSkeleton';
import { ChainType } from 'types/types';

interface IConversationList2 {
  className?: string;
  total: number;
  conversations: ChainType[];
  selectedId?: string | null;
  isCreate?: boolean;
  isFetchChainsLoading: boolean;

  onClickCard: (id: string) => void;
  onCreateConversation: () => void;
}

const ConversationList2 = ({
  total,
  conversations,
  onClickCard,
  selectedId = null,
  isCreate = false,
  onCreateConversation,
  isFetchChainsLoading,
  ...props
}: IConversationList2) => {
  const headline = `Цепочки сообщений · ${
    isFetchChainsLoading ? 'Загрузка...' : total
  }`;

  const handleClick = useCallback(
    ({ currentTarget }) => {
      const { dataset } = currentTarget;
      const { id } = dataset;
      onClickCard(id);
    },
    [onClickCard]
  );

  const handleCreateConversation = useCallback(() => {
    onCreateConversation();
  }, [onCreateConversation]);

  return (
    <StyledConversationList2 {...props}>
      <StyledTitle>
        <StyledHeadLine>{headline}</StyledHeadLine>
        <StyledButton onClick={handleCreateConversation}>
          <StyledCreateConversationIcon />
        </StyledButton>
      </StyledTitle>
      <StyledContainer>
        <StyledConversationCard2
          isHideCreateConversation={!isCreate}
          isCreateNewConversation
          isSelected
          title="Укажите тему"
        />
        {conversations &&
          conversations.map(({ id, to, ...otherProps }) => (
            <StyledConversationCard2
              key={id}
              data-id={id}
              onClick={handleClick}
              to={to}
              isSelected={id === selectedId}
              {...otherProps}
            />
          ))}
        {isFetchChainsLoading &&
          !conversations.length &&
          new Array(3).fill(null).map((_, idx) => <StyledChainSkeleton />)}
      </StyledContainer>
    </StyledConversationList2>
  );
};

const StyledConversationList2 = styled.div`
  background-color: transparent;
  width: 300px;
`;

const StyledTitle = styled.div`
  display: flex;
  flex-direction: row;
  height: 12px;
`;

const StyledHeadLine = styled.div`
  width: 140px;
  height: 15px;
  font-size: 12px;
  color: #919499;
  flex-grow: 1;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
`;

export const StyledButton = styled(Button)`
  padding: 0;
  border: none;
  background: none;
  height: 14px;
  max-width: 25px;
`;

const StyledCreateConversationIcon = styled(CreateConversation)`
  width: 22px;
  height: 15px;
`;

const StyledContainer = styled.div`
  margin-top: 7px;
`;

export const StyledConversationCard2 = styled(ConversationCard2)<{
  isHideCreateConversation?: boolean;
  isSelected: boolean;
}>`
  cursor: pointer;
  transition: height 0.6s ease-in-out, opacity 0.6s ease-in-out;

  ${({ isHideCreateConversation }) =>
    isHideCreateConversation &&
    `
    display: none;
  `}

  ${({ isSelected }) =>
    isSelected &&
    `
    box-shadow: 0 10px 15px 0 rgba(196, 194, 182, 0.9), 0 1px 2px 0 rgba(210, 208, 197, 0.9);
    transition: box-shadow 0.3s ease-in-out;
    padding-left: 5px;
    padding-bottom: 10px;
    border-bottom-left-radius: 4px;
    border-top-left-radius: 4px;
    padding-right: 25px;
    margin-right: -26px;
    position: relative;
    background-color: white;
  `}
`;

const StyledChainSkeleton = styled(ChainSkeleton)`
  &:first-of-type {
    margin-top: 10px;
  }
`;

export default memo(ConversationList2);
